import { FieldValues } from "react-hook-form";

import {
	RaRecord,
	useCreate,
	useNotify,
	useRecordContext,
	useRedirect,
	useUpdate,
} from "react-admin";
import { useCallback } from "react";

import * as R from "remeda";

type RedirectSettings = {
	resource: string;
	idColumn: string;
	view: "show" | "edit";
	subPath?: string;
};

const redirectPath = (
	resource: string,
	record: RaRecord,
	settings?: RedirectSettings
): string => {
	if (settings) {
		const { resource, idColumn, view, subPath } = settings;
		const id = record[idColumn];
		const path = `/${resource}/${id}/${view}`;

		return subPath ? `${path}/${subPath}` : path;
	} else {
		const id = record.id;

		return `/${resource}/${id}/show`;
	}
};

const handleErrorNotification = (error: any, notify: any) => {
	const errorMessage = error.body?.error || error.message;
	const isClientError = Math.floor(error.status / 100) === 4;

	const notificationMessage = isClientError
		? errorMessage
		: `Server communication error: ${errorMessage}`;

	notify(notificationMessage, {
		type: "error",
		messageArgs: { smart_count: 1 },
	});
};

export const useCreateSubmit = (
	resource: string,
	successMessage?: string,
	settings?: RedirectSettings,
	onSuccess: () => void = R.noop
) => {
	const redirect = useRedirect();
	const notify = useNotify();

	const [create] = useCreate();
	const save = useCallback(
		async (values: FieldValues) => {
			try {
				const record = await create(
					resource,
					{ data: values },
					{ returnPromise: true }
				);
				onSuccess();
				notify(successMessage || "ra.notification.created", {
					type: "info",
					messageArgs: { smart_count: 1 },
				});
				redirect(redirectPath(resource, record, settings));
			} catch (error: any) {
				if (error.body && error.body.errors) {
					return error.body.errors;
				}
				handleErrorNotification(error, notify);
			}
		},
		[create, notify, redirect, onSuccess, resource, successMessage, settings]
	);

	return save;
};

export const useUpdateSubmit = (
	resource: string,
	successMessage?: string,
	settings?: RedirectSettings,
	onSuccess: () => void = R.noop
) => {
	const redirect = useRedirect();
	const notify = useNotify();
	const record = useRecordContext();
	const [update] = useUpdate();
	const save = useCallback(
		async (values: FieldValues) => {
			try {
				await update(
					resource,
					{ id: record.id, data: values },
					{
						returnPromise: true,
					}
				);
				onSuccess();
				notify(successMessage || "ra.notification.updated", {
					type: "info",
					messageArgs: { smart_count: 1 },
				});
				redirect(redirectPath(resource, record, settings));
			} catch (error: any) {
				if (error.body && error.body.errors) {
					return error.body.errors;
				}

				handleErrorNotification(error, notify);
			}
		},
		[
			update,
			notify,
			redirect,
			onSuccess,
			resource,
			successMessage,
			record,
			settings,
		]
	);

	return save;
};
